import {
  EQUIPMENT_GROUP_ID,
  EQUIPMENT_GROUP_NAME,
  UTILITY_BILL_GROUP_ID,
  UTILITY_BILL_GROUP_NAME,
  UTILITY_BILL_CATEGORY_ID,
  EQUIPMENT_CATEGORY_ID,
  PROJECT_DOCUMENT_GROUP_ID,
  PROJECT_DOCUMENT_GROUP_NAME,
  PROJECT_DOCUMENT_CATEGORY_ID,
} from "./nonStandardDocumentUtils";
import { truthy } from "@src/global_functions/util";
import { EquipmentFileRow } from "@src/global_functions/backpackSdk/equipment/types";
import { DocumentsState } from "./types";
import { DesignDoc, DocumentGroup } from "@src/types";
import { ProjectAttachment } from "@src/global_functions/backpackSdk/projects/projectAttachments";

/**
 * Returns the plural `list` when it has entries; otherwise wraps a truthy
 * `singular` value in an array, falling back to an empty array. Used to
 * reconcile the legacy single equipment id/name fields with their plural
 * counterparts.
 */
export function firstNonEmpty<T>(
  list: T[] | undefined,
  singular: T | undefined | null
): T[] {
  if (list?.length) return list;
  if (singular) return [singular];
  return [];
}

export function formatEquipmentDocsAsDesignDocs(docs?: EquipmentFileRow[]) {
  return docs?.map<DocumentsState>((doc) => ({
    ...doc,
    category: EQUIPMENT_CATEGORY_ID,
    s3ObjectKey: "",
    lastAccessed: doc.uploadedAt,
    status: "",
    version: 1,
    uploadedBy: doc.uploadedBy ?? -1,
    noteAggr: doc.notes
      ?.map(
        (note) =>
          note.id && {
            id: note.id,
            description: note.description,
            page: note.page,
          }
      )
      .filter(truthy),
    groupAggr: [
      {
        groupId: EQUIPMENT_GROUP_ID,
        groupname: EQUIPMENT_GROUP_NAME,
        siteId: doc.siteId,
      },
    ],
    equipmentIds: firstNonEmpty(doc.equipmentIds, doc.equipmentId),
    equipmentNames: firstNonEmpty(doc.equipmentNames, doc.equipmentName),
  }));
}

export function formatProjectAttachmentsAsDesignDocs(
  docs: ProjectAttachment[] | undefined,
  siteId: number
) {
  return docs?.map<DocumentsState>((doc) => ({
    id: doc.id,
    filename: doc.filename,
    uploadedBy: -1,
    siteId,
    uploadedAt: doc.uploaded_at,
    lastAccessed: doc.uploaded_at,
    category: PROJECT_DOCUMENT_CATEGORY_ID,
    s3ObjectKey: "",
    fileType: "",
    status: "",
    hasPreview: doc.has_preview,
    noteAggr: [],
    groupAggr: [
      {
        groupId: PROJECT_DOCUMENT_GROUP_ID,
        groupname: PROJECT_DOCUMENT_GROUP_NAME,
        siteId: siteId,
      },
    ],
    version: doc.version,
    projectId: doc.project_id,
    projectName: doc.project_name,
  }));
}

export function formatDesignDocs(docs?: DesignDoc[]) {
  return docs?.map<DocumentsState>((doc) => ({
    ...doc,
    isDesignDoc: true,
    groupAggr:
      // add design docs with utility bill category into the utility bill group so they show up in the utility bill folder
      doc.category === UTILITY_BILL_CATEGORY_ID
        ? [
            {
              groupId: UTILITY_BILL_GROUP_ID,
              groupname: UTILITY_BILL_GROUP_NAME,
              siteId: doc.siteId,
            },
          ]
        : doc.groupAggr,
  }));
}

export function createDocumentGroups({
  siteId,
  addEquipmentGroup,
  addUtilityBillGroup,
  addProjectAttachmentGroup,
  designDocGroups,
}: {
  siteId: number;
  addEquipmentGroup: boolean;
  addUtilityBillGroup: boolean;
  addProjectAttachmentGroup: boolean;
  designDocGroups: DocumentGroup[];
}) {
  return [
    ...(addEquipmentGroup
      ? [
          {
            createdAt: new Date().toISOString(),
            createdBy: null,
            groupname: EQUIPMENT_GROUP_NAME,
            id: EQUIPMENT_GROUP_ID,
            parentGroup: null,
            siteId,
          },
        ]
      : []),
    ...(addUtilityBillGroup
      ? [
          {
            createdAt: new Date().toISOString(),
            createdBy: null,
            groupname: UTILITY_BILL_GROUP_NAME,
            id: UTILITY_BILL_GROUP_ID,
            parentGroup: null,
            siteId,
          },
        ]
      : []),
    ...(addProjectAttachmentGroup
      ? [
          {
            createdAt: new Date().toISOString(),
            createdBy: null,
            groupname: PROJECT_DOCUMENT_GROUP_NAME,
            id: PROJECT_DOCUMENT_GROUP_ID,
            parentGroup: null,
            siteId,
          },
        ]
      : []),
    ...designDocGroups,
  ];
}
